import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {ProcessFlowchartStaticNode, ProcessStaticFlowchart} from '../ProcessFlowchartVizConstants';
import {
  getClientIdForProcessFlowchartEdge,
  getCollapsedNodeClientId,
} from './ConvertProcessFlowchartForReactFlowUtil';
import {makeCollapsedNode} from './makeProcessFlowchartStaticNode';

interface StackElement {
  currentNodeId: string;
  lastDefinedNodeId: string;
  nodeIdsInPath: Array<string>;
}

class CollapseNodesWithNoTasksRunner {
  flowchart: ProcessStaticFlowchart;
  newFlowchart: ProcessStaticFlowchart;
  loopHeadsAndPaths: Array<{loopHeadId: string; nodesInPathIncludingLoop: Array<string>}>;
  // The IDs of new (collapsed) nodes, mapped to the IDs of the nodes in collapsed paths that they are representing
  collapsedNodeIdToNodeIdsInCollapsedPaths: Record<string, Set<string>>;
  // All nodes that we are collapsing (this should be the union of all nodeIdsInPath), mapped to the ids of the new collapsed nodes representing them
  collapsingNodeIdsToCollapsedNodeIds: Record<string, string>;

  constructor(flowchart: ProcessStaticFlowchart) {
    this.flowchart = flowchart;
    this.collapsedNodeIdToNodeIdsInCollapsedPaths = {};
    this.loopHeadsAndPaths = [];
    this.newFlowchart = {
      clientId: this.flowchart.clientId,
      nodes: [],
      edges: [],
    };
    this.collapsingNodeIdsToCollapsedNodeIds = {};
  }

  run(): ProcessStaticFlowchart {
    const rootNode = this.flowchart.nodes.find(
      (node) => node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
    assert(rootNode != null, 'Expected root node to be defined', {flowchart: this.flowchart});

    const stack: Array<StackElement> = [
      {
        currentNodeId: rootNode.clientId,
        lastDefinedNodeId: rootNode.clientId,
        nodeIdsInPath: [],
      },
    ];

    this.newFlowchart = {
      clientId: this.flowchart.clientId,
      nodes: [...this.flowchart.nodes],
      edges: [],
    };
    const visitedNodes = new Set<string>();

    while (stack.length > 0) {
      const {currentNodeId, lastDefinedNodeId, nodeIdsInPath} = stack.pop()!;
      const currentNode = this.newFlowchart.nodes.find(({clientId}) => clientId === currentNodeId);
      assert(currentNode != null, 'Expected node to exist', {
        currentNodeId,
        lastDefinedNodeId,
        nodeIdsInPath,
        flowchart: this.flowchart,
        newFlowchart: this.newFlowchart,
      });

      // We want to collapse paths before we check if we have visited the node so that we collapse looping paths which have no data
      const currentNodeHasData = this.nodeHasData(currentNode);
      if ((lastDefinedNodeId !== currentNodeId || nodeIdsInPath.length > 0) && currentNodeHasData) {
        this.collapsePathIntoNode({lastDefinedNodeId, currentNodeId, nodeIdsInPath});
      }

      // Breaking cycles
      // If the current node has data and we have already visited it, we don't need to re-enqueue it because
      // this should be the end of the current path and we should have already covered all paths from this node
      // when we visited it previously. Re-enqueuing it would cause us to double count nodes and potentially
      // infinite loop in graphs with cycles.
      // If the current node does not have data and it's already in the path, we don't re-enqueue it since
      // that means there's a cycle in the graph.
      if (currentNodeHasData && visitedNodes.has(currentNodeId)) {
        continue;
      } else if (!currentNodeHasData && nodeIdsInPath.includes(currentNodeId)) {
        this.loopHeadsAndPaths.push({
          loopHeadId: currentNodeId,
          nodesInPathIncludingLoop: nodeIdsInPath,
        });
        continue;
      }

      visitedNodes.add(currentNodeId);

      let nodeHasEdges = false;
      for (const edge of this.flowchart.edges) {
        if (edge.fromNodeClientId !== currentNodeId) {
          continue;
        }

        nodeHasEdges = true;
        stack.unshift({
          currentNodeId: edge.toNodeClientId,
          lastDefinedNodeId: currentNodeHasData ? currentNodeId : lastDefinedNodeId,
          nodeIdsInPath: currentNodeHasData ? [] : [...nodeIdsInPath, currentNodeId],
        });
      }

      /*
       * If we have reached the end of the flowchart, but we have not found a defined node along the current path, we need to collapse that path.
       * Note that all paths from a single source node which end without finding a node with instance data will be aggregated into a single collapsed node
       *
       *            / --> B(1) -> C(1)
       * root --> A(1) --> D --> E
       *             \ --> F --> G
       *
       * In the graph above, we would create a single collapsed node starting at A which includes D, E, F, and G.
       *
       *            / --> B(1) -> C(1)
       * root --> A(1)
       *             \ --> Collapsed (4)
       *
       */
      if (!nodeHasEdges && !this.nodeHasData(currentNode)) {
        this.collapsePathIntoNode({
          lastDefinedNodeId,
          currentNodeId: null,
          nodeIdsInPath: [...nodeIdsInPath, currentNodeId],
        });
      }
    }

    const finalFlowchart = this.removeOrphanedNodesAndEdgesAndSetCollapsedNodeNames();
    return finalFlowchart;
  }

  private collapsePathIntoNode({
    lastDefinedNodeId,
    currentNodeId,
    nodeIdsInPath,
  }: Omit<StackElement, 'currentNodeId'> & {currentNodeId: string | null}): void {
    // if there are no nodes without data in the path, we just add an edge from the last node with data to the current node
    if (nodeIdsInPath.length === 0) {
      assert(currentNodeId != null, 'Expected current node to be defined');
      const edgeInOriginalFlowchart = this.flowchart.edges.find(
        ({fromNodeClientId, toNodeClientId}) =>
          fromNodeClientId === lastDefinedNodeId && toNodeClientId === currentNodeId
      );

      this.newFlowchart.edges.push({
        ...(edgeInOriginalFlowchart ?? {}),
        clientId: getClientIdForProcessFlowchartEdge(lastDefinedNodeId, currentNodeId),
        fromNodeClientId: lastDefinedNodeId,
        toNodeClientId: currentNodeId,
      });
      return;
    }

    // This loop checks for whether we've already collapsed one of the nodes in nodeIdsInPath, which can occur when the node is part of a loop.
    // Otherwise, nothing occurs in this loop.
    for (const nodeToCollapse of nodeIdsInPath) {
      const newCollapsedNodeId = this.collapsingNodeIdsToCollapsedNodeIds[nodeToCollapse];
      // If any of the nodes we're trying to collapse have been collapsed before
      if (newCollapsedNodeId != null) {
        // We draw an edge from the collapsed node to the next node with data / output node / end node, if relevant
        if (currentNodeId != null) {
          const newEdgeClientId = getClientIdForProcessFlowchartEdge(newCollapsedNodeId, currentNodeId);
          if (!this.newFlowchart.edges.some(({clientId}) => newEdgeClientId === clientId)) {
            this.newFlowchart.edges.push({
              clientId: newEdgeClientId,
              fromNodeClientId: newCollapsedNodeId,
              toNodeClientId: currentNodeId,
            });
          }
        }
        // We also want to count all the nodes in this path as nodes that have been collapsed into newCollapsedNode
        for (const nodeId of nodeIdsInPath) {
          this.collapsedNodeIdToNodeIdsInCollapsedPaths[newCollapsedNodeId].add(nodeId);
        }
        // We then don't want to do any more collapsing of the nodes in this path, so we return
        return;
      }
    }

    // if we found a node with data, we add an edge from the last upstream node to this node
    const collapsedNodeId = getCollapsedNodeClientId(lastDefinedNodeId, currentNodeId);
    for (const nodeId of nodeIdsInPath) {
      this.collapsingNodeIdsToCollapsedNodeIds[nodeId] = collapsedNodeId;
    }
    if (this.collapsedNodeIdToNodeIdsInCollapsedPaths[collapsedNodeId] == null) {
      this.collapsedNodeIdToNodeIdsInCollapsedPaths[collapsedNodeId] = new Set(nodeIdsInPath);
      /*
       * If there is at least one node without data in the path, we add a new "collapsed node" in their place.
       * We want to insert this node at the index of the first node it is collapsing, so the order remains intact.
       * For instance, for the following flowchart:
       *
       *            / --> B(1)
       * root --> Router
       *            \ --> A
       *
       * Nodes: [root, Router, A, B]
       *
       * We want to collapse into the following:
       *
       *            / --> B(1)
       * root --> Router
       *            \ --> Collapsed (1)
       *
       * Nodes: [root, Router, Collapsed, A (orphaned), B]
       *
       * Rather than:
       *
       *            / --> Collapsed (1)
       * root --> Router
       *            \ --> B(1)
       *
       * Nodes: [root, Router, A (orphaned), B, Collapsed]
       * (Here, node B comes before the collapsed node so it would be rendered on the left)
       */
      const index = this.flowchart.nodes.findIndex(({clientId}) => clientId === nodeIdsInPath[0]);
      this.newFlowchart.nodes.splice(index, 0, makeCollapsedNode(collapsedNodeId));
      this.newFlowchart.edges.push({
        clientId: getClientIdForProcessFlowchartEdge(lastDefinedNodeId, collapsedNodeId),
        fromNodeClientId: lastDefinedNodeId,
        toNodeClientId: collapsedNodeId,
      });
      if (currentNodeId != null) {
        this.newFlowchart.edges.push({
          clientId: getClientIdForProcessFlowchartEdge(collapsedNodeId, currentNodeId),
          fromNodeClientId: collapsedNodeId,
          toNodeClientId: currentNodeId,
        });
      }
    } else {
      /*
       * If we have already created a collapsed node between the last node with data and the current node, we just need to update the number of nodes in the path.
       * This case handles graph shapes which branch and then merge into a single point
       *
       *            / --> B(1) -> C(1)  \
       * root --> A(1) --> D --> E   -> output
       *             \ --> F --> G     /
       *
       * For example, in the graph above we would create a single collapsed node between A and output which includes the four
       * nodes D, E, F, and G.
       *
       *            / --> B(1) -> C(1)  \
       * root --> A(1)                 output
       *             \  (Collapsed 4)   /
       *
       */
      const collapsedNode = this.newFlowchart.nodes.find((node) => node.clientId === collapsedNodeId);
      assert(collapsedNode != null, 'Expected collapsed node to be defined', {
        flowchart: this.flowchart,
        newFlowchart: this.newFlowchart,
        collapsedNodeId,
        lastDefinedNodeId,
        currentNodeId,
        nodeIdsInPath,
      });
      for (const nodeId of nodeIdsInPath) {
        this.collapsedNodeIdToNodeIdsInCollapsedPaths[collapsedNodeId].add(nodeId);
      }
    }
  }

  private removeOrphanedNodesAndEdgesAndSetCollapsedNodeNames(): ProcessStaticFlowchart {
    const finalFlowchart: ProcessStaticFlowchart = {
      clientId: this.newFlowchart.clientId,
      nodes: [],
      edges: [...this.newFlowchart.edges],
    };

    for (const node of this.newFlowchart.nodes) {
      // Do not add any nodes that are not connected to the graph
      const newNode = {...node};
      if (
        finalFlowchart.edges.some(
          (edge) => edge.toNodeClientId === newNode.clientId || edge.fromNodeClientId === newNode.clientId
        )
      ) {
        finalFlowchart.nodes.push(newNode);
      }
      if (newNode.outboundEdgeIdToLabel != null) {
        const oldOutboundEdgeIdToLabel = newNode.outboundEdgeIdToLabel;
        newNode.outboundEdgeIdToLabel = {};
        for (const edgeId of Object.keys(oldOutboundEdgeIdToLabel)) {
          const edge = finalFlowchart.edges.find((edge) => edge.clientId === edgeId);
          if (edge != null) {
            newNode.outboundEdgeIdToLabel[edgeId] = oldOutboundEdgeIdToLabel[edgeId];
          }
        }
      }
    }

    for (const [collapsedNodeId, nodeIdsInPath] of Object.entries(
      this.collapsedNodeIdToNodeIdsInCollapsedPaths
    )) {
      const collapsedNode = finalFlowchart.nodes.find((node) => node.clientId === collapsedNodeId);
      if (collapsedNode == null) {
        continue;
      }
      const relevantLoops = this.loopHeadsAndPaths.filter(({loopHeadId}) => nodeIdsInPath.has(loopHeadId));
      const nodeIdsInLoops = new Set(
        relevantLoops.map(({nodesInPathIncludingLoop}) => nodesInPathIncludingLoop).flat()
      );
      const allNodeIds = new Set([...nodeIdsInPath, ...nodeIdsInLoops]);
      collapsedNode.name = `${allNodeIds.size}`;
      collapsedNode.numNodesRepresented = allNodeIds.size;
    }

    return finalFlowchart;
  }

  private nodeHasData(node: ProcessFlowchartStaticNode): boolean {
    if (node.type === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) {
      return (node.statusCounts ?? []).some((statusCount) => statusCount.count > 0);
    } else if (node.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
      return this.flowchart.edges.some(
        ({fromNodeClientId, hasBeenUsedToCreateData}) =>
          fromNodeClientId === node.clientId && hasBeenUsedToCreateData
      );
    }
    return true;
  }
}

/**
 * Creates a new flowchart which "collapses" paths in the flowchart which do not have any instance data.
 *
 * For example, given a flowchart
 *
 * root --> A --> B --> C --> D --> output
 *
 * Where only the root and A nodes have any tasks associated with, this function would return a new function of the form
 *
 * root --> A --> Collapsed (3) --> output
 *
 * Where the `Collapsed` node is of type `COLLAPSED_NODE` and represents the nodes B, C, and D which have no data.
 *
 * See https://docs.google.com/document/d/1w81atSkRAskZ_jgdFpjSznVaX1ZXahSPzeQsbEJTiBU/edit?usp=sharing for more details on how this function transforms
 * flowcharts with different, more complex topologies.
 *
 * @param flowchart The flowchart to reformat
 * @returns A new flowchart with collapsed nodes
 */
export default function collapseNodesWithNoTasksInFlowchart(
  flowchart: ProcessStaticFlowchart
): ProcessStaticFlowchart {
  const collapseRunner = new CollapseNodesWithNoTasksRunner(flowchart);
  return collapseRunner.run();
}
